/**
 * Async Product Cards Loader
 * 
 * This script handles loading product cards asynchronously and reinitializing
 * wishlist buttons after the cards are loaded.
 */
(function() {
  'use strict';
  
  const SECTION_ID = 'async-product-card';
  const BATCH_SIZE = 4; // Number of cards to load in parallel
  const INTERSECTION_THRESHOLD = 0.1; // Load when 10% visible
  const ROOT_MARGIN = '200px'; // Start loading 200px before visible
  
  const loadedProducts = new Set();
  const pendingLoads = new Map();
  
  function init() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(handleIntersection, {
        rootMargin: ROOT_MARGIN,
        threshold: INTERSECTION_THRESHOLD
      });
      
      document.querySelectorAll('[data-async-product-card]').forEach(card => {
        observer.observe(card);
      });
      
      window.asyncProductCardsObserver = observer;
    } else {
      loadAllCards();
    }
  }
  
  function handleIntersection(entries, observer) {
    const cardsToLoad = [];
    
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const card = entry.target;
        const productHandle = card.dataset.productHandle;
        
        if (!loadedProducts.has(productHandle) && !pendingLoads.has(productHandle)) {
          cardsToLoad.push(card);
        }
        
        observer.unobserve(card);
      }
    });
    
    // Load cards in batches
    if (cardsToLoad.length > 0) {
      loadCardsBatch(cardsToLoad);
    }
  }
  
  async function loadCardsBatch(cards) {
    // Process cards in batches
    for (let i = 0; i < cards.length; i += BATCH_SIZE) {
      const batch = cards.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(card => loadCard(card)));

       document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
    }
  }
  
  async function loadCard(cardElement) {
    const productHandle = cardElement.dataset.productHandle;
    const productId = cardElement.dataset.productId;
    const oldTagStyle = cardElement.dataset.oldTagStyle === 'true';
    const showDiscount = cardElement.dataset.showDiscount === 'true';
    const collectionHandle = cardElement.dataset.collectionHandle || '';
    
    // Mark as pending
    pendingLoads.set(productHandle, true);
    
    try {
      // Build the URL for section rendering API
      let url = `/products/${productHandle}?section_id=${SECTION_ID}`;
      if (oldTagStyle) url += '&old_tag_style=true';
      if (showDiscount) url += '&show_discount=true';
      if (collectionHandle) url += `&collection=${collectionHandle}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to load product card: ${response.status}`);
      }
      
      const html = await response.text();
      
      // Parse the HTML and extract the product card
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCard = doc.querySelector('[data-component="product-card"]');
      
      if (newCard) {
        // Replace the placeholder with the real card
        cardElement.parentNode.replaceChild(newCard, cardElement);
        
        // Initialize the component
        initializeProductCard(newCard);
        
        // Reinitialize wishlist button
        reinitializeWishlist(newCard, productId);
        
        // Initialize Reviews.io if present
        initializeReviewsWidget(newCard);
        
        // Mark as loaded
        // loadedProducts.add(productHandle);
      }
    } catch (error) {
      console.error(`Error loading product card for ${productHandle}:`, error);
      // Mark card as failed but keep it visible
      cardElement.classList.add('product-card--async-failed');
    } finally {
      pendingLoads.delete(productHandle);
    }
  }
  
  function initializeProductCard(cardElement) {
    // Initialize Core component if available
    if (window.Core && window.Core.Components) {
      try {
        // Re-parse and initialize the component
        Core.Components._createInstance(cardElement);
      } catch (e) {
        console.debug('Product card component init:', e);
      }
    }
    
    // Initialize mobile slider if present
    const mobileSlider = cardElement.querySelector('.product_card__image-mobile-slider.swiper');
    if (mobileSlider && window.Swiper) {
      new Swiper(mobileSlider, {
        slidesPerView: 1,
        loop: false,
        pagination: false
      });
    }
  }
  
  function reinitializeWishlist(cardElement, productId) {
    const wishlistBtn = cardElement.querySelector('.swym-button.swym-add-to-wishlist-view-product');
    
    if (!wishlistBtn) return;
    
    // Helper that safely runs the Swym refresh without exploding when Swym isn't ready yet
    const tryRefresh = (swatInstance = window._swat) => {
      if (!swatInstance) return false;
      try {
        if (typeof swatInstance.initializeActionButtons === 'function' && swatInstance.ui) {
          swatInstance.initializeActionButtons('[data-swaction]');
          return true;
        }
        if (typeof swatInstance.ui === 'object' && typeof swatInstance.ui.refresh === 'function') {
          swatInstance.ui.refresh();
          return true;
        }
      } catch (e) {
        console.debug('Swym refresh failed:', e);
      }
      return false;
    };
    
    if (tryRefresh()) {
      return;
    }
    
    if (window.SwymCallbacks) {
      window.SwymCallbacks.push(function(swat) {
        tryRefresh(swat);
      });
    }
    
    const event = new CustomEvent('async-product-card:loaded', {
      detail: { 
        productId: productId,
        cardElement: cardElement
      },
      bubbles: true
    });
    cardElement.dispatchEvent(event);
  }
  
  function initializeReviewsWidget(cardElement) {
    const reviewWidget = cardElement.querySelector('.reviewsio--rating-snippet');
    if (reviewWidget && window.reviewsio_widgets) {
      try {
        // Trigger Reviews.io to scan for new widgets
        if (typeof window.reviewsio_widgets.init === 'function') {
          window.reviewsio_widgets.init();
        }
      } catch (e) {
        console.debug('Reviews.io init failed:', e);
      }
    }
  }
  
  function loadAllCards() {
    const cards = document.querySelectorAll('[data-async-product-card]');
    loadCardsBatch(Array.from(cards));

     document.dispatchEvent(new CustomEvent("swym:collections-loaded"));

  }
  
  function refreshAsyncCards(container = document) {
    if (window.asyncProductCardsObserver) {
      container.querySelectorAll('[data-async-product-card]').forEach(card => {
        if (!loadedProducts.has(card.dataset.productHandle)) {
          window.asyncProductCardsObserver.observe(card);
        }
      });
    }
  }
  
  function forceLoadCard(productHandle) {
    const card = document.querySelector(`[data-async-product-card][data-product-handle="${productHandle}"]`);
    if (card && !loadedProducts.has(productHandle)) {
      loadCard(card);
    }
  }
  
  // Expose public API
  window.AsyncProductCards = {
    init: init,
    refresh: refreshAsyncCards,
    forceLoad: forceLoadCard
  };
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Re-initialize after Shopify section re-renders
  document.addEventListener('shopify:section:load', function(event) {
    refreshAsyncCards(event.target);
  });
  
})();
